import { z } from "zod";
import { ValidationError } from "../errors/ValidationError";
import { dispatchValidationError } from "../errors/errorDispatcher";
import { isValidVatNumber } from "../../utils/vatNumberUtils";

const alphaWithApostrophe = /^[A-Za-zÀ-ÖØ-öø-ÿ' -]{1,50}$/;
const businessNamePattern = /^[A-Za-z0-9À-ÖØ-öø-ÿ'&.,() -]{1,120}$/;
const sdiPattern = /^[A-Z0-9]{6,7}$/;
const phoneDigitsPattern = /^\d{9,15}$/;
const taxCodePattern =
  /^(\d{11}|[A-Z]{6}[\dLMNPQRSTUV]{2}[ABCDEHLMPRST][\dLMNPQRSTUV]{2}[A-Z][\dLMNPQRSTUV]{3}[A-Z])$/i;
const requiredVatNumberSchema = z
  .string()
  .trim()
  .min(1, "Campo obbligatorio")
  .refine(isValidVatNumber, "Campo non valido");
const optionalVatNumberSchema = z.union([
  z.literal(""),
  z.string().trim().refine(isValidVatNumber, "Campo non valido"),
]);
const PHONE_DIGITS_MESSAGE = "Inserisci da 9 a 15 cifre (solo numeri).";
const SFTP_URL_MAX = 2048;

export const WEBSITE_URL_INVALID_MESSAGE = "Url non valido";
export const WEBSITE_HTTPS_ONLY_MESSAGE = "Usare solo indirizzi https.";

export const PEC_SDI_AT_LEAST_ONE_MESSAGE = "Compilare almento uno tra PEC e SDI";

const optionalEmail = z.email().optional();

const WEBSITE_MAX = 2048;

function websiteAuthorityHasNonAscii(candidate) {
  const m = candidate.match(/^https:\/\/([^/?#]*)/i);
  if (!m) {
    return false;
  }
  for (const ch of m[1]) {
    if (ch.codePointAt(0) > 127) {
      return true;
    }
  }
  return false;
}

function websiteHostLabelsMatchJavaUriHostname(host) {
  const labels = host.split(".");
  for (const label of labels) {
    if (label.length === 0 || label.length > 63) {
      return false;
    }
    const lower = label.toLowerCase();
    if (lower.startsWith("xn--")) {
      if (lower.length < 4 || !/^xn--[a-z0-9-]+$/.test(lower)) {
        return false;
      }
      continue;
    }
    if (label.startsWith("-") || label.endsWith("-")) {
      return false;
    }
    for (const c of label) {
      if (!/[A-Za-z0-9-]/.test(c)) {
        return false;
      }
    }
  }
  return true;
}

function isWebsiteHostWwwValid(host) {
  const hl = host.toLowerCase();
  if (hl.includes(".www.")) {
    return false;
  }
  if (hl.endsWith(".www")) {
    return false;
  }
  if (hl.startsWith("www.")) {
    const rest = host.slice(4);
    if (!rest.length) {
      return false;
    }
    const labels = rest.split(".");
    if (labels.length < 2) {
      return false;
    }
    for (const label of labels) {
      if (label.length === 0) {
        return false;
      }
    }
  }
  return true;
}

function normalizeWebsiteCandidate(value) {
  const httpsMatch = value.match(/^https:\/\//i);
  if (httpsMatch) {
    return `https://${value.slice(httpsMatch[0].length)}`;
  }
  if (value.includes("://")) {
    return null;
  }
  return `https://${value}`;
}

export function validateVendorWebsiteUserInput(raw) {
  const t = (raw ?? "").trim();
  if (!t.length) {
    return { ok: false, message: "Campo obbligatorio" };
  }
  if (t.length > WEBSITE_MAX) {
    return { ok: false, message: WEBSITE_URL_INVALID_MESSAGE };
  }

  const lower = t.toLowerCase();
  if (lower.startsWith("http://") && !lower.startsWith("https://")) {
    return { ok: false, message: WEBSITE_HTTPS_ONLY_MESSAGE };
  }

  const candidate = normalizeWebsiteCandidate(t);
  if (!candidate) {
    return { ok: false, message: WEBSITE_URL_INVALID_MESSAGE };
  }

  if (candidate.length > WEBSITE_MAX) {
    return { ok: false, message: WEBSITE_URL_INVALID_MESSAGE };
  }

  if (websiteAuthorityHasNonAscii(candidate)) {
    return { ok: false, message: WEBSITE_URL_INVALID_MESSAGE };
  }

  let u;
  try {
    u = new URL(candidate);
  } catch {
    return { ok: false, message: WEBSITE_URL_INVALID_MESSAGE };
  }
  if (u.protocol !== "https:") {
    return { ok: false, message: WEBSITE_URL_INVALID_MESSAGE };
  }
  if (u.username || u.password) {
    return { ok: false, message: WEBSITE_URL_INVALID_MESSAGE };
  }
  const host = u.hostname;
  if (!host?.includes(".")) {
    return { ok: false, message: WEBSITE_URL_INVALID_MESSAGE };
  }
  if (!websiteHostLabelsMatchJavaUriHostname(host)) {
    return { ok: false, message: WEBSITE_URL_INVALID_MESSAGE };
  }
  if (!isWebsiteHostWwwValid(host)) {
    return { ok: false, message: WEBSITE_URL_INVALID_MESSAGE };
  }
  const hl = host.toLowerCase();
  if (hl === "localhost" || hl.endsWith(".localhost")) {
    return { ok: false, message: WEBSITE_URL_INVALID_MESSAGE };
  }
  const lastDot = host.lastIndexOf(".");
  if (lastDot < 0 || lastDot >= host.length - 1) {
    return { ok: false, message: WEBSITE_URL_INVALID_MESSAGE };
  }
  const tld = host.slice(lastDot + 1);
  if (tld.length < 2 || !/^\p{L}+$/u.test(tld)) {
    return { ok: false, message: WEBSITE_URL_INVALID_MESSAGE };
  }

  return { ok: true, normalized: candidate };
}

export function validateVendorSftpCatalogUrlInput(raw) {
  const t = (raw ?? "").trim();
  if (!t.length) {
    return { ok: false, message: "Campo obbligatorio" };
  }
  if (t.length > SFTP_URL_MAX) {
    return { ok: false, message: WEBSITE_URL_INVALID_MESSAGE };
  }

  if (t.toLowerCase().startsWith("sftp://")) {
    try {
      const parsed = new URL(t);
      if (parsed.protocol !== "sftp:" || !parsed.hostname) {
        return { ok: false, message: WEBSITE_URL_INVALID_MESSAGE };
      }
      return { ok: true, normalized: t };
    } catch {
      return { ok: false, message: WEBSITE_URL_INVALID_MESSAGE };
    }
  }

  return validateVendorWebsiteUserInput(t);
}

export function normalizeVendorWebsiteForBackend(rawInput) {
  const t = (rawInput ?? "").trim();
  if (!t.length) {
    return null;
  }
  const r = validateVendorWebsiteUserInput(t);
  return r.ok ? r.normalized : null;
}

export const websiteUrlPatchField = z.preprocess((v) => {
  if (v === undefined || v === null) {
    return undefined;
  }
  if (typeof v !== "string") {
    return v;
  }
  const t = v.trim();
  return t.length ? t : undefined;
}, z.string().max(WEBSITE_MAX).optional());

export const modificaAccountPatchRequestSchema = z
  .object({
    referringName: z.string().trim().regex(alphaWithApostrophe, "Campo non valido").optional(),
    referringSurname: z.string().trim().regex(alphaWithApostrophe, "Campo non valido").optional(),
    email: optionalEmail,
    role: z.literal("vendor"),
    businessName: z.string().trim().regex(businessNamePattern, "Campo non valido").optional(),
    tradeName: z.string().trim().regex(businessNamePattern, "Campo non valido").optional(),
    taxCode: z
      .union([
        z.literal(""),
        z.string().trim().regex(taxCodePattern, "Codice fiscale non valido"),
      ])
      .optional(),
    vatNumber: optionalVatNumberSchema.optional(),
    websiteUrl: websiteUrlPatchField,
    phoneNumber: z.string().trim().regex(phoneDigitsPattern, PHONE_DIGITS_MESSAGE).optional(),
    refPhoneNumber: z
      .union([z.literal(""), z.string().trim().regex(phoneDigitsPattern, PHONE_DIGITS_MESSAGE)])
      .optional(),
    pubEmail: z
      .union([
        z.null(),
        z.string().trim().pipe(z.union([z.literal(""), z.email("Campo non valido")])),
      ])
      .optional(),
    refEmail: z
      .union([
        z.null(),
        z.string().trim().pipe(z.union([z.literal(""), z.email("Campo non valido")])),
      ])
      .optional(),
    sftpServerUrl: z.string().trim().max(SFTP_URL_MAX).optional(),
    catalogUsername: z.string().optional(),
    catalogPassword: z.string().optional(),
    pec: z
      .union([
        z.null(),
        z
          .string()
          .trim()
          .pipe(z.union([z.literal(""), z.email("Campo non valido")])),
      ])
      .optional(),
    sdiCode: z
      .union([
        z.null(),
        z
          .string()
          .trim()
          .transform((s) => s.toUpperCase())
          .pipe(z.union([z.literal(""), z.string().regex(sdiPattern, "Campo non valido")])),
      ])
      .optional(),
    logo: z
      .string()
      .regex(
        /^data:image\/(png|jpeg);base64,[A-Za-z0-9+/=]+$/,
        { message: "Invalid image format. Must be base64 PNG or JPEG." }
      )
      .nullable()
      .optional(),
    clearLogo: z.literal(true).optional(),
    addressVia: z.string().trim().max(255).optional(),
    addressCivico: z.string().trim().max(64).optional(),
    addressCap: z.string().trim().max(16).optional(),
    addressComune: z.string().trim().max(255).optional(),
    addressProvincia: z.string().trim().max(255).optional(),
    addressRegione: z.string().trim().max(255).optional(),
    addressStato: z.string().trim().max(255).optional(),
  })
  .superRefine((data, ctx) => {
    if (data.websiteUrl !== undefined) {
      const r = validateVendorWebsiteUserInput(data.websiteUrl);
      if (!r.ok) {
        ctx.addIssue({
          code: "custom",
          path: ["websiteUrl"],
          message: r.message,
        });
      }
    }
    if (data.sftpServerUrl !== undefined && data.sftpServerUrl) {
      const rs = validateVendorSftpCatalogUrlInput(data.sftpServerUrl);
      if (!rs.ok) {
        ctx.addIssue({
          code: "custom",
          path: ["sftpServerUrl"],
          message: rs.message,
        });
      }
    }
    const editableKeys = [
      "referringName",
      "referringSurname",
      "email",
      "businessName",
      "tradeName",
      "taxCode",
      "vatNumber",
      "websiteUrl",
      "phoneNumber",
      "refPhoneNumber",
      "pubEmail",
      "refEmail",
      "sftpServerUrl",
      "catalogUsername",
      "catalogPassword",
      "pec",
      "sdiCode",
      "logo",
      "clearLogo",
      "addressVia",
      "addressCivico",
      "addressCap",
      "addressComune",
      "addressProvincia",
      "addressRegione",
      "addressStato",
    ];
    const hasAtLeastOnePatchField =
      data.clearLogo === true ||
      editableKeys
        .filter((key) => key !== "clearLogo")
        .some((key) => {
          const v = data[key];
          if (v === undefined) return false;
          if (key === "logo" && v === null) return false;
          return true;
        });
    if (!hasAtLeastOnePatchField) {
      ctx.addIssue({
        code: "custom",
        path: ["role"],
        message: "at least one field must be provided for patch update",
      });
    }
  });

export const MAX_ELIMINA_MOTIVATION_LEN = 2000;

export const eliminaAccountRequestSchema = z.object({
  role: z.literal("vendor"),
  motivation: z.string().trim().max(MAX_ELIMINA_MOTIVATION_LEN).optional(),
});

export function validateEliminaAccountRequest(data) {
  const parsed = eliminaAccountRequestSchema.safeParse(data);
  if (!parsed.success) {
    const message = `Invalid eliminaAccount payload: ${parsed.error.issues[0]?.message ?? "unknown error"}`;
    dispatchValidationError({ message, issues: parsed.error.issues, raw: parsed.error });
    throw new ValidationError(message, { issues: parsed.error.issues, raw: parsed.error });
  }
  return parsed.data;
}

export function validateModificaAccountRequest(data) {
  const parsed = modificaAccountPatchRequestSchema.safeParse(data);
  if (!parsed.success) {
    const message = `Invalid modificaAccount payload: ${parsed.error.issues[0]?.message ?? "unknown error"}`;
    dispatchValidationError({ message, issues: parsed.error.issues, raw: parsed.error });
    throw new ValidationError(message, { issues: parsed.error.issues, raw: parsed.error });
  }
  const result = { ...parsed.data };
  if (result.websiteUrl !== undefined) {
    const r = validateVendorWebsiteUserInput(result.websiteUrl);
    if (r.ok && r.normalized) {
      result.websiteUrl = r.normalized;
    }
  }
  if (result.sftpServerUrl !== undefined && result.sftpServerUrl) {
    const r = validateVendorSftpCatalogUrlInput(result.sftpServerUrl);
    if (r.ok && r.normalized) {
      result.sftpServerUrl = r.normalized;
    }
  }
  return result;
}

export const editVendorUnifiedSchema = z
  .object({
    nomeReferente: z
      .string()
      .trim()
      .min(1, "Campo obbligatorio")
      .regex(alphaWithApostrophe, "Campo non valido"),
    cognomeReferente: z
      .string()
      .trim()
      .min(1, "Campo obbligatorio")
      .regex(alphaWithApostrophe, "Campo non valido"),
    nomeCommerciale: z
      .string()
      .trim()
      .min(1, "Campo obbligatorio")
      .regex(businessNamePattern, "Campo non valido"),
    ragioneSociale: z
      .string()
      .trim()
      .min(1, "Campo obbligatorio")
      .regex(businessNamePattern, "Campo non valido"),
    codiceFiscale: z
      .union([
        z.literal(""),
        z.string().trim().regex(taxCodePattern, "Codice fiscale non valido"),
      ]),
    indirizzoEmail: z.email("Campo non valido"),
    indirizzoPec: z.union([z.email("Campo non valido"), z.literal("")]),
    codiceSDI: z.union([z.string().trim().toUpperCase().regex(sdiPattern, "Campo non valido"), z.literal("")]),
    numeroTelefono: z
      .string()
      .trim()
      .min(1, "Campo obbligatorio")
      .regex(phoneDigitsPattern, PHONE_DIGITS_MESSAGE),
    indirizzoVia: z.string().trim().max(255).optional().default(""),
    indirizzoCivico: z.string().trim().max(64).optional().default(""),
    indirizzoCap: z.string().trim().max(16).optional().default(""),
    indirizzoCitta: z.string().trim().max(120).optional().default(""),
    indirizzoProvincia: z.string().trim().max(255).optional().default(""),
    indirizzoRegione: z.string().trim().max(255).optional().default(""),
    indirizzoStato: z.string().trim().max(255).optional().default(""),
    sitowebAziendale: z.string().trim().min(1, "Campo obbligatorio").max(WEBSITE_MAX),
    partitaIVA: requiredVatNumberSchema,
    emailReferente: z.union([z.literal(""), z.email("Campo non valido")]),
    telefonoReferente: z.union([
      z.literal(""),
      z.string().trim().regex(phoneDigitsPattern, PHONE_DIGITS_MESSAGE),
    ]),
    urlSftp: z.string().trim().max(WEBSITE_MAX).optional().default(""),
    utenteSftp: z.string().optional().default(""),
    passwordSftp: z.string().optional().default(""),
    emailPubblica: z.union([z.literal(""), z.email("Campo non valido")]),
  })
  .superRefine((data, ctx) => {
    const pecTrim = (data.indirizzoPec ?? "").trim();
    const sdiTrim = (data.codiceSDI ?? "").trim();
    if (!pecTrim && !sdiTrim) {
      ctx.addIssue({
        code: "custom",
        path: ["indirizzoPec"],
        message: PEC_SDI_AT_LEAST_ONE_MESSAGE,
      });
      ctx.addIssue({
        code: "custom",
        path: ["codiceSDI"],
        message: PEC_SDI_AT_LEAST_ONE_MESSAGE,
      });
    }
    const w = validateVendorWebsiteUserInput(data.sitowebAziendale);
    if (!w.ok) {
      ctx.addIssue({
        code: "custom",
        path: ["sitowebAziendale"],
        message: w.message,
      });
    }
    const sftp = (data.urlSftp ?? "").trim();
    if (sftp.length > 0) {
      const r = validateVendorSftpCatalogUrlInput(sftp);
      if (!r.ok) {
        ctx.addIssue({
          code: "custom",
          path: ["urlSftp"],
          message: r.message,
        });
      }
    }
  });

export const editVendorAziendaleSchema = editVendorUnifiedSchema;
export const editVendorReferenteSchema = editVendorUnifiedSchema;
